import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/ui/Icon';
import Footer from '../homepage/components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { sellerService } from '../../services/sellerService';
import toast from 'react-hot-toast';

export default function ManufacturerSignup() {
  const navigate = useNavigate();
  const { user: currentUser, refreshUserProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Auth data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Company details
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [capabilities, setCapabilities] = useState('3d_printing'); // '3d_printing', 'cnc', or 'both'
  const [selectedTier, setSelectedTier] = useState('factory'); // DEFAULT to the most popular 'factory' tier!
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Active Terms & Agreements
  const [activeTerms, setActiveTerms] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    const fetchManufacturerTerms = async () => {
      try {
        const terms = await sellerService.getActiveTermsVersionByType('manufacturer');
        setActiveTerms(terms);
      } catch (err) {
        console.warn('Failed to load manufacturer T&C from database:', err);
      }
    };
    fetchManufacturerTerms();
  }, []);

  // If already logged in, skip Step 1
  useEffect(() => {
    if (currentUser) {
      setStep(2);
      setEmail(currentUser.email || '');
      setBusinessEmail(currentUser.email || '');
      setFirstName(currentUser.user_metadata?.first_name || '');
      setLastName(currentUser.user_metadata?.last_name || '');
    }
  }, [currentUser]);

  const handleStep1Submit = (e) => {
    e?.preventDefault();
    setError('');

    if (!firstName || !lastName || !email || !password) {
      setError('Please fill in all credentials fields.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setStep(2);
  };

  const handleSignupSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    if (!businessName || !businessEmail) {
      setError('Please provide company name and contact email.');
      setLoading(false);
      return;
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy.');
      setLoading(false);
      return;
    }

    try {
      let activeUser = currentUser;

      // 1. Create Supabase Auth User if not logged in
      if (!activeUser) {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName,
              role: 'Manufacturer'
            }
          }
        });

        if (signUpError) throw signUpError;
        activeUser = signUpData?.user;

        if (!activeUser) {
          throw new Error('Sign up succeeded but user session could not be established.');
        }

        // Wait a small delay for user record trigger synchronization
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Map dynamic monthly fee amount based on tier selection
      const feeMap = { workshop: 15.0, factory: 25.0, foundry: 59.0 };
      const monthlyServiceFee = feeMap[selectedTier] || 15.0;

      // Calculate annual expiration date (1 year from today!)
      const expiresAt = new Date();
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
      const subscriptionExpiresAt = expiresAt.toISOString();

      // 2. Create manufacturer_accounts row (resilient fallback for pending database sync!)
      let mfgAccount = null;
      let mfgAccountError = null;

      try {
        const { data, error } = await supabase
          .from('manufacturer_accounts')
          .insert({
            user_id: activeUser.id,
            business_name: businessName,
            business_email: businessEmail,
            business_phone: businessPhone || null,
            tier: selectedTier,
            monthly_service_fee: monthlyServiceFee,
            subscription_expires_at: subscriptionExpiresAt,
            account_status: 'active' // Auto-active for direct dashboard flow
          })
          .select()
          .single();
        
        mfgAccount = data;
        mfgAccountError = error;
      } catch (insertErr) {
        mfgAccountError = insertErr;
      }

      // Check if it failed due to missing tier/fee columns in schema cache (PGRST204)
      if (mfgAccountError && (mfgAccountError.code === 'PGRST204' || mfgAccountError.message?.includes('tier') || mfgAccountError.message?.includes('fee') || mfgAccountError.message?.includes('expires'))) {
        console.warn('⚠️ PostgREST schema cache is missing subscription fields. Retrying insert with fallback layout...');
        
        // Try fallback 2: with only tier if fee/expires fails
        try {
          const { data, error } = await supabase
            .from('manufacturer_accounts')
            .insert({
              user_id: activeUser.id,
              business_name: businessName,
              business_email: businessEmail,
              business_phone: businessPhone || null,
              tier: selectedTier,
              account_status: 'active'
            })
            .select()
            .single();
          
          mfgAccount = data;
          mfgAccountError = error;
        } catch (fb2Err) {
          mfgAccountError = fb2Err;
        }

        // Try fallback 3: completely clean without any subscription/tier columns
        if (mfgAccountError && (mfgAccountError.code === 'PGRST204' || mfgAccountError.message?.includes('tier'))) {
          console.warn('⚠️ Fallback 2 failed, retrying insert completely clean...');
          const { data, error } = await supabase
            .from('manufacturer_accounts')
            .insert({
              user_id: activeUser.id,
              business_name: businessName,
              business_email: businessEmail,
              business_phone: businessPhone || null,
              account_status: 'active'
            })
            .select()
            .single();
          
          mfgAccount = data;
          mfgAccountError = error;
        }
      }

      if (mfgAccountError) throw mfgAccountError;

      // 3. Create public manufacturers row (symmetrical writes) - WITHOUT missing email/phone columns!
      const { error: manufacturerError } = await supabase
        .from('manufacturers')
        .insert({
          name: businessName,
          manufacturer_account_id: mfgAccount.id,
          is_active: true
        });

      if (manufacturerError) throw manufacturerError;

      // 4. Create initial manufacturing_supplier_configs
      const { error: configError } = await supabase
        .from('manufacturing_supplier_configs')
        .insert({
          manufacturer_id: mfgAccount.id,
          supplier_name: businessName,
          supplier_type: capabilities, // '3d_printing', 'cnc', or 'both'
          email_notifications_enabled: true,
          api_integration_enabled: false,
          is_active: true,
          capabilities: {
            supplier_type: capabilities,
            machines: [],
            materials: []
          }
        });

      if (configError) throw configError;

      // 5. Record agreement signature if active terms are loaded (Resilient against 409 duplicates!)
      if (activeTerms?.id) {
        try {
          const { data: existingSig } = await supabase
            .from('seller_agreements')
            .select('id')
            .eq('user_id', activeUser.id)
            .eq('version_id', activeTerms.id)
            .maybeSingle();

          if (!existingSig) {
            await sellerService.recordSellerAgreement(activeUser.id, activeTerms.id);
          } else {
            console.log('User has already signed this agreement version. Skipping duplicate insert to prevent 409 conflict.');
          }
        } catch (sigErr) {
          console.warn('Failed to record manufacturer agreement signature:', sigErr);
        }
      }

      setSuccessMessage('Manufacturer account registered successfully!');
      toast.success('Manufacturer registration completed!');
      
      // Refresh context and redirect to manufacturing dashboard
      if (refreshUserProfile) await refreshUserProfile();
      
      setTimeout(() => {
        navigate('/manufacturing-dashboard');
      }, 1500);

    } catch (err) {
      console.error('Manufacturer signup error:', err);
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const manufacturerTiers = [
    {
      id: 'workshop',
      name: 'The Workshop',
      badge: 'Entry Level',
      price: '$15.00',
      description: 'Perfect for small workshops and single-machine setups.',
      features: [
        'Up to 2 active machines',
        'Email order notifications',
        'Standard FDM/SLA materials',
        'Basic dashboard analytics'
      ]
    },
    {
      id: 'factory',
      name: 'The Factory',
      badge: 'Most Popular',
      price: '$25.00',
      description: 'Ideal for professional workshops with medium production capacity.',
      features: [
        'Unlimited active machines (3D/CNC)',
        'Includes all "Workshop" features',
        'Priority queue routing on catalog',
        'Symmetrical directory badge'
      ]
    },
    {
      id: 'foundry',
      name: 'The Foundry',
      badge: 'Enterprise Level',
      price: '$59.00',
      description: 'Built for industrial fabrication facilities with bulk queues.',
      features: [
        'Symmetrical webhooks & API access',
        'Includes all "Factory" features',
        'White-glove account manager',
        'Custom contract queue matching'
      ]
    }
  ];

  const containerClass = step === 1 ? 'max-w-md' : 'max-w-4xl';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      
      <main className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className={`w-full space-y-8 bg-white p-8 rounded-2xl border border-slate-200 shadow-xl transition-all duration-300 ${containerClass}`}>
          <div className="text-center">
            <div className="inline-flex p-3 bg-indigo-50 text-indigo-600 rounded-full mb-4">
              <Icon name="Settings" size={32} />
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Manufacturer Signup
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Join OpenGadget as an authorized manufacturing partner
            </p>
          </div>

          {/* Stepper Progress bar */}
          <div className="flex items-center justify-center space-x-4 my-6">
            <span className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-colors ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700'}`}>1</span>
            <div className={`h-0.5 w-12 transition-all ${step >= 2 ? 'bg-indigo-600' : 'bg-slate-200'}`}></div>
            <span className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-colors ${step >= 2 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700'}`}>2</span>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-semibold flex items-center gap-2">
              <Icon name="XCircle" size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm font-semibold flex items-center gap-2 animate-pulse">
              <Icon name="CheckCircle" size={18} className="shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-4">
              <h3 className="text-lg font-bold text-slate-800">1. Account Credentials</h3>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name *"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                />
                <Input
                  label="Last Name *"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
              <Input
                label="Email Address *"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john.doe@company.com"
                required
              />
              <Input
                label="Password *"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <Button type="submit" variant="primary" className="w-full mt-6 py-2.5 text-white bg-indigo-600 hover:bg-indigo-700 font-bold">
                Continue to Company Info →
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSignupSubmit} className="space-y-6 text-left">
              <h3 className="text-lg font-bold text-slate-800 border-b pb-2 mb-4">2. Company Profile</h3>
              
              {/* Company Details 2-Column Form block */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input
                  label="Company / Workshop Name *"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Apex Manufacturing Lab"
                  required
                />
                
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">Capabilities Offered *</label>
                  <Select
                    value={capabilities}
                    onChange={setCapabilities}
                    options={[
                      { value: '3d_printing', label: '3D Printing Only' },
                      { value: 'cnc', label: 'CNC Machining Only' },
                      { value: 'both', label: 'Both 3D Printing & CNC' }
                    ]}
                  />
                </div>

                <Input
                  label="Contact Email *"
                  type="email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  placeholder="production@apex.com"
                  required
                />
                
                <Input
                  label="Contact Phone"
                  value={businessPhone}
                  onChange={(e) => setBusinessPhone(e.target.value)}
                  placeholder="+1 (555) 019-2834"
                />
              </div>

              {/* Horizontal Tiers Selection Grid spanning across full width in a 3-column single row structure */}
              <div className="border-t border-slate-100 pt-5 mt-6">
                <label className="block text-sm font-bold text-slate-700 mb-3">Partnership Tier *</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {manufacturerTiers.map(tier => {
                    const isSelected = selectedTier === tier.id;
                    return (
                      <div 
                        key={tier.id}
                        onClick={() => setSelectedTier(tier.id)}
                        className={`relative flex flex-col rounded-xl border-2 p-5 cursor-pointer transition-all h-full justify-between ${
                          isSelected 
                            ? 'border-indigo-600 bg-indigo-50/40 ring-1 ring-indigo-500/10' 
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              tier.id === 'factory' ? 'text-indigo-600 bg-indigo-100' : 'text-slate-500 bg-slate-100'
                            }`}>
                              <Icon name={tier.id === 'workshop' ? 'Star' : tier.id === 'factory' ? 'TrendingUp' : 'Award'} size={18} />
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 font-bold uppercase rounded-full ${
                              tier.id === 'factory' 
                                ? 'bg-indigo-600 text-white animate-pulse' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {tier.badge}
                            </span>
                          </div>

                          <h3 className="text-base font-extrabold text-slate-900 mb-1">{tier.name}</h3>
                          <p className="text-xs text-slate-500 leading-relaxed mb-4">{tier.description}</p>
                          
                          {/* 4 distinct offerings list */}
                          <ul className="space-y-2.5 mt-3 border-t border-slate-100 pt-3">
                            {tier.features.map((feature, idx) => (
                              <li key={idx} className="text-slate-600 text-xs flex items-start leading-snug">
                                <Icon name="Check" size={14} className="text-emerald-500 mr-1.5 shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="text-xl font-black text-indigo-600 mt-6 border-t border-slate-100 pt-3">
                          {tier.price}
                          <span className="text-xs text-slate-400 font-normal"> / month</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Agreement and Buttons wrapper */}
              <div className="pt-4 border-t border-slate-100 space-y-4">
                <div>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="rounded text-indigo-600 border-slate-300 mt-1"
                    />
                    <span className="text-xs text-slate-600 leading-snug">
                      I agree to the{' '}
                      {activeTerms ? (
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="text-indigo-600 underline font-bold hover:text-indigo-800"
                        >
                          Manufacturer Partnership Agreement (T&C)
                        </button>
                      ) : (
                        <Link to="/terms-of-service" className="text-indigo-600 underline">Terms of Service</Link>
                      )}{' '}
                      and the <Link to="/privacy-policy" className="text-indigo-600 underline">Privacy Policy</Link> as an authorized platform partner.
                    </span>
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  {!currentUser && (
                    <Button type="button" variant="outline" onClick={() => setStep(1)} className="w-1/4">
                      ← Back
                  </Button>
                  )}
                  <Button type="submit" variant="primary" disabled={loading} className="flex-1 text-white bg-indigo-600 hover:bg-indigo-700 font-bold">
                    {loading ? 'Submitting Application...' : 'Submit Application'}
                  </Button>
                </div>
              </div>
            </form>
          )}

          <div className="text-center mt-6 pt-6 border-t border-slate-200">
            <span className="text-xs text-slate-500">Already registered as a partner? </span>
            <Link to="/signin" className="text-xs text-indigo-600 font-bold hover:underline">Sign In</Link>
          </div>
        </div>
      </main>

      {/* T&C Viewer Modal */}
      {showTermsModal && activeTerms && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl border border-slate-200">
            <div className="p-6 border-b flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Manufacturer Partnership Agreement</h3>
                <p className="text-xs text-slate-500">Version {activeTerms.versionString} • Active</p>
              </div>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="p-1.5 hover:bg-slate-200 rounded-full transition-colors text-slate-500"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto prose prose-slate max-w-none text-slate-600 text-sm leading-relaxed whitespace-pre-line">
              {activeTerms.content}
            </div>
            <div className="p-4 border-t bg-slate-50 flex justify-end gap-3 rounded-b-2xl">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAgreedToTerms(false);
                  setShowTermsModal(false);
                }}
              >
                Decline
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  setAgreedToTerms(true);
                  setShowTermsModal(false);
                }}
                className="text-white bg-indigo-600 hover:bg-indigo-700 font-bold"
              >
                I Agree
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
